import { useEffect, useState, useCallback } from 'react';
import { Eraser, Download, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';

interface Candidato {
  tipo: 'candidato' | 'furo';
  modelo: '55' | '65';
  serie: string;
  numero: number;
  ano: number;
  status: string;
  codigo_status: string | null;
  motivo_status: string | null;
  emitido_em: string | null;
  valor_total: string | null;
  orcamento_id: number | null;
  venda_resolvida: boolean;
}

interface FecharResult {
  ok: boolean;
  persistido: boolean;
  cStat: string | null;
  xMotivo: string;
  nProt: string | null;
  inutId: number | null;
}

interface InutFeita {
  id: number;
  modelo: string;
  serie: string;
  numero_ini: number;
  numero_fin: number;
  ano: number;
  c_stat: string | null;
  n_prot: string | null;
  x_motivo: string | null;
  justificativa: string;
  usuario_nome: string | null;
  criado_em: string;
  tem_xml: boolean;
}

const fmtData = (s: string) => {
  try {
    const d = new Date(s);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
};
const fmtModelo = (m: string) => (m === '65' ? 'NFC-e' : 'NF-e');
const fmtMoney = (v: string | null) =>
  v == null ? '—' : `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function InutilizacoesTab() {
  const toast = useToast();
  const dialog = useDialog();
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [feitas, setFeitas] = useState<InutFeita[]>([]);
  const [loading, setLoading] = useState(true);
  const [fechandoKey, setFechandoKey] = useState<string | null>(null);
  const [fechandoLote, setFechandoLote] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  const keyDe = (c: Candidato) => `${c.modelo}-${c.serie}-${c.numero}`;
  function toggleUm(key: string) {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  const todosMarcados = candidatos.length > 0 && candidatos.every((c) => selecionados.has(keyDe(c)));
  const algunsMarcados = selecionados.size > 0 && !todosMarcados;
  function toggleTodos() {
    setSelecionados(todosMarcados ? new Set<string>() : new Set(candidatos.map(keyDe)));
  }

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [cand, hist] = await Promise.all([
        api.get<Candidato[]>('/api/inutilizacoes/candidatos'),
        api.get<InutFeita[]>('/api/inutilizacoes'),
      ]);
      setCandidatos(cand);
      setFeitas(hist);
      setSelecionados(new Set());
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function fechar(c: Candidato) {
    const ok = await dialog.confirmar(
      `Inutilizar na SEFAZ o numero ${c.numero} (serie ${c.serie}, ${fmtModelo(c.modelo)})?\n\n` +
      `Acao definitiva — o numero nunca mais podera ser emitido. A SEFAZ recusa se ele estiver autorizado, entao e seguro.`,
      { perigoso: true, textoConfirmar: 'Inutilizar' },
    );
    if (!ok) return;
    const key = `${c.modelo}-${c.serie}-${c.numero}`;
    setFechandoKey(key);
    try {
      const r = await api.post<FecharResult>('/api/inutilizacoes/fechar', {
        modelo: c.modelo, serie: c.serie, numero: c.numero, ano: c.ano,
      });
      if (r.cStat === '102') toast.sucesso(`Numero ${c.numero} inutilizado na SEFAZ`);
      else if (r.cStat === '563' || r.cStat === '256') toast.sucesso(`Numero ${c.numero} ja estava inutilizado (confirmado)`);
      else toast.erro(`SEFAZ recusou: ${r.xMotivo} (cStat ${r.cStat ?? '—'})`);
      await carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setFechandoKey(null);
    }
  }

  async function fecharSelecionados() {
    const alvos = candidatos.filter((c) => selecionados.has(keyDe(c)));
    if (alvos.length === 0) return;
    const ok = await dialog.confirmar(
      `Inutilizar na SEFAZ os ${alvos.length} numeros selecionados?\n\n` +
      `Acao definitiva — cada numero nunca mais podera ser emitido. A SEFAZ recusa os que ja estiverem autorizados, entao e seguro.`,
      { perigoso: true, textoConfirmar: `Inutilizar ${alvos.length}` },
    );
    if (!ok) return;
    setFechandoLote(true);
    let inut = 0, jaEstava = 0, recusados = 0;
    for (const c of alvos) {
      setFechandoKey(keyDe(c));
      try {
        const r = await api.post<FecharResult>('/api/inutilizacoes/fechar', {
          modelo: c.modelo, serie: c.serie, numero: c.numero, ano: c.ano,
        });
        if (r.cStat === '102') inut++;
        else if (r.cStat === '563' || r.cStat === '256') jaEstava++;
        else recusados++;
      } catch {
        recusados++;
      }
    }
    setFechandoKey(null);
    setFechandoLote(false);
    const partes: string[] = [];
    if (inut) partes.push(`${inut} inutilizado(s)`);
    if (jaEstava) partes.push(`${jaEstava} ja estava(m)`);
    if (recusados) partes.push(`${recusados} recusado(s)`);
    const resumo = partes.join(' · ') || 'Nada a fazer';
    if (recusados) toast.erro(resumo);
    else toast.sucesso(resumo);
    await carregar();
  }

  async function baixarXml(it: InutFeita) {
    try {
      const xml = await api.get<string>(`/api/inutilizacoes/${it.id}/xml`);
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const faixa = it.numero_fin !== it.numero_ini ? `${it.numero_ini}-${it.numero_fin}` : String(it.numero_ini);
      a.download = `inut_${it.modelo}_${it.serie}_${faixa}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Explicacao */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Quando uma emissao e recusada pela SEFAZ e a venda e reemitida no numero seguinte, o numero antigo vira um{' '}
        <strong>buraco</strong> na sequencia — o contador aponta como "Notas nao lancadas de Saidas". Aqui voce{' '}
        <strong>fecha os buracos</strong> inutilizando os numeros na SEFAZ e baixa os XMLs pro contador. So aparecem
        numeros que comprovadamente <strong>nao foram autorizados</strong>.
      </div>

      {/* ---- Buracos a fechar ---- */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Eraser size={16} /> Buracos a fechar
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>({candidatos.length})</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {selecionados.size > 0 && (
              <button type="button" className="btn-primario" onClick={() => void fecharSelecionados()}
                disabled={fechandoLote || loading || fechandoKey !== null}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                {fechandoLote ? <Loader2 size={13} className="animate-spin" /> : <Eraser size={13} />}
                Fechar selecionados ({selecionados.size})
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={() => void carregar()} disabled={loading || fechandoLote}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px' }}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : undefined} /> Atualizar
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando...</div>
        ) : candidatos.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, fontSize: 13, color: 'var(--text-muted)' }}>
            <CheckCircle2 size={16} style={{ color: 'var(--primary)' }} /> Nenhum buraco a fechar. A sequencia esta limpa.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--panel-bg)', textAlign: 'left' }}>
                  <Th>Modelo</Th><Th>Serie</Th><Th>Numero</Th><Th>Quando</Th><Th>Valor</Th>
                  <Th>Situacao</Th>
                  <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={todosMarcados}
                        ref={(el) => { if (el) el.indeterminate = algunsMarcados; }}
                        onChange={toggleTodos}
                        style={{ cursor: 'pointer' }}
                        aria-label="Selecionar todos"
                      />
                      Venda
                    </label>
                  </th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {candidatos.map((c) => {
                  const key = `${c.modelo}-${c.serie}-${c.numero}`;
                  const marcado = selecionados.has(key);
                  return (
                    <tr key={key} style={{ borderTop: '1px solid var(--border)', background: marcado ? 'color-mix(in srgb, var(--primary) 10%, transparent)' : undefined }}>
                      <Td>{fmtModelo(c.modelo)}</Td>
                      <Td>{c.serie}</Td>
                      <Td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.numero}</Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>{c.emitido_em ? fmtData(c.emitido_em) : '—'}</Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>{fmtMoney(c.valor_total)}</Td>
                      <Td>
                        {c.tipo === 'furo'
                          ? 'Numero pulado — sem registro na sequencia'
                          : c.status === 'rejeitada'
                          ? 'Rejeitada (schema) — numero livre'
                          : `Sem retorno — SEFAZ confirmou: nao consta (${c.codigo_status})`}
                        {c.motivo_status && (
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.motivo_status}>
                            {c.motivo_status}
                          </div>
                        )}
                      </Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => toggleUm(key)}
                            style={{ cursor: 'pointer' }}
                            aria-label={`Selecionar numero ${c.numero}`}
                          />
                          {c.tipo === 'furo' ? (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                          ) : c.venda_resolvida ? (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ja emitida em outro nº</span>
                          ) : (
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#fde68a', color: '#000', padding: '2px 6px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <AlertTriangle size={11} /> sem NF ainda
                            </span>
                          )}
                        </label>
                      </Td>
                      <Td>
                        <button type="button" className="btn-primario" onClick={() => void fechar(c)} disabled={fechandoKey !== null || fechandoLote}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                          {fechandoKey === key ? <Loader2 size={13} className="animate-spin" /> : <Eraser size={13} />}
                          Fechar
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---- Inutilizacoes feitas ---- */}
      <section>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <CheckCircle2 size={16} /> Inutilizacoes feitas
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>({feitas.length})</span>
        </h3>
        {feitas.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 2px' }}>Nada inutilizado ainda.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--panel-bg)', textAlign: 'left' }}>
                  <Th>Quando</Th><Th>Modelo</Th><Th>Serie</Th><Th>Numero(s)</Th>
                  <Th>Resultado</Th><Th>Protocolo</Th><Th>Por</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {feitas.map((it) => (
                  <tr key={it.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td style={{ whiteSpace: 'nowrap' }}>{fmtData(it.criado_em)}</Td>
                    <Td>{fmtModelo(it.modelo)}</Td>
                    <Td>{it.serie}</Td>
                    <Td style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {it.numero_fin !== it.numero_ini ? `${it.numero_ini}–${it.numero_fin}` : it.numero_ini}
                    </Td>
                    <Td>
                      {it.c_stat === '102' ? 'Inutilizada' : (it.c_stat === '563' || it.c_stat === '256') ? 'Ja estava inutilizada' : `cStat ${it.c_stat}`}
                    </Td>
                    <Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{it.n_prot || '—'}</Td>
                    <Td>{it.usuario_nome || '—'}</Td>
                    <Td>
                      {it.tem_xml && (
                        <button type="button" className="btn-secundario" onClick={() => void baixarXml(it)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                          <Download size={13} /> XML
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Td({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 10px', verticalAlign: 'top', ...style }}>{children}</td>;
}
